import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessSettings } from '../hooks/useBusinessSettings';

export default function FirstRunWizard() {
  const { currentBusiness, API_BASE_URL, fetchCsrfToken } = useAuth() as any;
  const { updateSettings } = useBusinessSettings();
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    business_name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    phone: ''
  });
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const saveProfile = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const csrf = await fetchCsrfToken();

      // Ensure a business exists; if none, create one (admin-only route)
      let businessId = currentBusiness?.id;
      if (!businessId) {
        const createRes = await fetch(`${API_BASE_URL}/businesses`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(csrf ? { 'X-CSRF-Token': csrf } : {})
          },
          body: JSON.stringify({
            name: form.business_name,
            street: form.street,
            city: form.city,
            state: form.state,
            zip: form.zip,
            phone: form.phone
          })
        });
        if (!createRes.ok) throw new Error('Failed to create business');
        const created = await createRes.json();
        businessId = created.id;
      }

      const res = await fetch(`${API_BASE_URL}/businesses/${businessId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        },
        body: JSON.stringify({
          name: form.business_name || currentBusiness.name,
          street: form.street,
          city: form.city,
          state: form.state,
          zip: form.zip,
          phone: form.phone
        })
      });
      if (!res.ok) throw new Error('Failed to save business profile');

      // Ensure settings row exists; keep it minimal (no changes)
      await updateSettings({});

      setStep(2);
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const complete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE_URL}/auth/complete-first-run`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        }
      });
      if (!res.ok) throw new Error('Failed to complete setup');
      window.location.replace('/dashboard');
    } catch (e: any) {
      setError(e?.message || 'Failed to complete setup');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-6 lg:px-8 py-8" data-testid="first-run-wizard">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
        <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
          <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white">Let's get your account setup!</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-neutral-300">Step {step} of 2</p>
        </div>

        <div className="p-6">
          {error ? (
            <div role="alert" className="mb-4 border border-red-300 bg-red-50 text-red-800 rounded-lg p-3 text-sm">{error}</div>
          ) : null}

          {step === 1 ? (
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); saveProfile(); }}>
              <div>
                <label htmlFor="business_name" className="block text-sm font-semibold uppercase tracking-wide text-gray-600">Business Name</label>
                <input id="business_name" name="business_name" autoComplete="organization" required value={form.business_name} onChange={onChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
              </div>

              <div>
                <label htmlFor="street" className="block text-sm font-semibold uppercase tracking-wide text-gray-600">Street Address</label>
                <input id="street" name="street" autoComplete="address-line1" required value={form.street} onChange={onChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-semibold uppercase tracking-wide text-gray-600">City</label>
                  <input id="city" name="city" autoComplete="address-level2" required value={form.city} onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-semibold uppercase tracking-wide text-gray-600">State</label>
                  <input id="state" name="state" autoComplete="address-level1" required value={form.state} onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
                </div>
                <div>
                  <label htmlFor="zip" className="block text-sm font-semibold uppercase tracking-wide text-gray-600">Zip Code</label>
                  <input id="zip" name="zip" autoComplete="postal-code" required value={form.zip} onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold uppercase tracking-wide text-gray-600">Phone Number</label>
                <input id="phone" name="phone" autoComplete="tel" required value={form.phone} onChange={onChange}
                  className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white" />
              </div>

              <p className="text-xs text-gray-500">You can edit this information later within settings.</p>

              <div className="mt-4 flex items-center gap-3" data-testid="first-run-stepper">
                <button type="submit" disabled={submitting}
                  className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="first-run-next">
                  {submitting ? 'Saving…' : 'Next'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-700 dark:text-neutral-200">
                <p>Thanks for choosing Sedra. We are currently in closed beta, and the website is subject to change. Please use the Closed Beta Portal in the bottom left of the page to report bugs or suggest new ideas! RECOMMENDED USAGE! Create the service you offer within Items/Pricing and add it to a category for easy sorting! Then you can add your customers in and begin invoicing with Sedra! Note that for the Mass Create feature for invoicing, it assumes that the customer exists and the service exists! You can add customer-specific pricing within the customer detail page for each customer! <strong>ENSURE THAT THE ITEM HAS CUSTOMER SPECIFIC PRICING ENABLED!</strong></p>
              </div>

              <label className="flex items-start gap-3">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 text-sedra focus:ring-sedra" />
                <span className="text-sm text-gray-700 dark:text-neutral-200">I understand the above and am ready to continue.</span>
              </label>

              <div className="pt-2">
                <button onClick={complete} disabled={!agree || submitting}
                  className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="first-run-finish">
                  {submitting ? 'Finishing…' : 'Finish'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
} 